import { toast } from 'react-toastify';
import { getContractBody } from '../get-contract-body';
import axios from 'axios';
import { Network, solididtyReleases } from '../types';
import { solidityCompiler } from '@agnostico/browser-solidity-compiler';
import { BigNumber, ContractFactory, ethers, providers, Wallet } from 'ethers';
import { UNISWAP_V2_ROUTER_ABI, ERC20_TOKEN_ABI, UNISWAP_V2_FACTORY_ABI, UNISWAP_V2_PAIR_ABI } from './abis';

const solidityVersion = '0.8.25';
export const SUPPLY = 420690000000;
export const DECIMALS = 9;
export const ROUTER_ADDRESS = '0x7a250d5630b4cf539739df2c5dacb4c659f2488d';
export const WETH_ADDRESS = '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2';
export const FACTORY_ADDRESS = '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f';

const MAX_FEE_PER_GAS_MULTIPLIER_PERCENTAGE = 55;
const MAX_PRIORITY_FEE_PER_GAS_MULTIPLIER_PERCENTAGE = 35;

interface NetworkSettings {
    maxFeePerGas: BigNumber;
    maxPriorityFeePerGas: BigNumber;
    gasLimit: number;
}

const getNetworkSettings = async (ethProvider: providers.JsonRpcProvider): Promise<NetworkSettings> => {
    const feeData = await ethProvider.getFeeData();

    return {
        maxFeePerGas: feeData.maxFeePerGas
            ? feeData.maxFeePerGas.mul(100 + MAX_FEE_PER_GAS_MULTIPLIER_PERCENTAGE).div(100)
            : BigNumber.from(ethers.utils.parseUnits('1', 'gwei')),
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            ? feeData.maxPriorityFeePerGas.mul(100 + MAX_PRIORITY_FEE_PER_GAS_MULTIPLIER_PERCENTAGE).div(100)
            : BigNumber.from(ethers.utils.parseUnits('1', 'gwei')),
        gasLimit: 3_000_000,
    };
};

export const verifyEtherscan = async (
    contractAddress: string,
    contractBody: string,
    network: Network,
    solidityReleases: solididtyReleases,
    contractName: string,
) => {
    const etherscanApiUrl: Record<Network, string> = {
        fantom: 'https://api.ftmscan.com/api',
        mainnet: 'https://api.etherscan.io/api',
    };
    const scanApiKey: Record<Network, string | undefined> = {
        fantom: process.env.REACT_APP_ETHERSCAN_API_KEY,
        mainnet: process.env.REACT_APP_ETHERSCAN_API_KEY,
    };
    if (contractAddress === undefined) {
        toast.error('Contract is not deployed!');
        return;
    }
    toast.info('Trying to verify...');

    await axios
        .post(
            etherscanApiUrl[network],
            {
                apikey: scanApiKey[network],
                module: 'contract',
                action: 'verifysourcecode',
                contractaddress: contractAddress,
                sourceCode: contractBody,
                codeformat: 'solidity-single-file',
                contractname: contractName,
                compilerversion: solidityReleases[solidityVersion].replace('soljson-', '').replace('.js', ''),
                optimizationUsed: 1,
                runs: 200,
                licenseType: 3,
            },
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            },
        )
        .then((response) => {
            response.data.message === 'OK' ? toast.success('Contract verified!') : toast.error('Failed to verify!');
            console.log(response.data);
        });
};

export const deployETHToken = async (
    initLiquidity: string,
    description: string,
    name: string,
    symbol: string,
    ethPrivateKey: string,
    ethProvider: providers.JsonRpcProvider,
    solidityReleases: solididtyReleases,
    tokensToCA: string,
    snipeParams?: snipeParams,
): Promise<string[]> => {
    toast.info(`Deploying ${name} token...`);
    const contractName = symbol.split(/\s+/)[0];
    const contractBody = await getContractBody(description, solidityVersion, contractName, name, symbol, tokensToCA, SUPPLY, DECIMALS);

    const [bytecode, abi] = await solidityCompiler({
        version: `https://binaries.soliditylang.org/bin/${solidityReleases[solidityVersion]}`,
        contractBody,
        options: {
            optimizer: {
                enabled: true,
                runs: 200,
            },
        },
    }).then((result: any) => {
        console.log(result);
        console.log('Compiled');
        console.log(`all names`, Object.keys(result.contracts.Compiled_Contracts));
        console.log('my contract', result.contracts.Compiled_Contracts[contractName]);

        return [result.contracts.Compiled_Contracts[contractName].evm.bytecode.object, result.contracts.Compiled_Contracts[contractName].abi];
    });

    const wallet = new Wallet(ethPrivateKey, ethProvider);

    const contractFactory = new ContractFactory(abi, bytecode, wallet);

    const value = ethers.utils.parseEther(initLiquidity);

    const gas = await getNetworkSettings(ethProvider);

    const nonce = await wallet.provider.getTransactionCount(wallet.address);
    const contract = await contractFactory.deploy({ value: value, ...gas, nonce: nonce });
    const hash = contract.deployTransaction.hash;
    toast.info(`Deployment transaction sent: ${hash}`);

    if (snipeParams)
        openTrading({
            ...snipeParams,
            deployerPrivateKey: ethPrivateKey,
            ethProvider: ethProvider,
            deployedContractAddress: contract.address,
            tokensToCAPercantage: Number(tokensToCA),
            ethReserve: value,
            tokenReserve: ethers.utils.parseUnits(BigNumber.from(SUPPLY).mul(tokensToCA).div(100).toString(), DECIMALS),
            gas: {
                maxFeePerGas: gas.maxFeePerGas.sub(1),
                maxPriorityFeePerGas: gas.maxPriorityFeePerGas.sub(1),
                gasLimit: gas.gasLimit,
            },
            nonce: nonce + 1,
        });
    await contract.deployed();

    console.log(`Contract deployed at address: ${contract.address}`);
    toast.info(`Contract deployed at address: ${contract.address}`);

    return [contract.address, contractBody];
};

interface OpenTradingParams {
    deployerPrivateKey: string;
    ethProvider: providers.JsonRpcProvider;
    deployedContractAddress: string;
    tokensToCAPercantage: number;
    ethReserve?: BigNumber;
    tokenReserve?: BigNumber;
    gas?: NetworkSettings;
    nonce?: number;
    sniperPrivateKeys?: string[];
    sniperBuyTokenAmounts?: BigNumber[];
}

interface snipeParams {
    sniperPrivateKeys: string[];
    sniperBuyTokenAmounts: BigNumber[];
}

export const openTrading = async ({
    deployerPrivateKey,
    ethProvider,
    deployedContractAddress,
    tokensToCAPercantage,
    ethReserve,
    tokenReserve,
    gas,
    nonce,
    sniperPrivateKeys,
    sniperBuyTokenAmounts,
}: OpenTradingParams) => {
    toast.info('Open Trading...');

    const wallet = new Wallet(deployerPrivateKey, ethProvider);

    const tokenContract = new ethers.Contract(deployedContractAddress, ERC20_TOKEN_ABI, wallet);
    gas = gas ?? (await getNetworkSettings(ethProvider));

    ethReserve = ethReserve ?? (await wallet.provider.getBalance(deployedContractAddress));
    tokenReserve = tokenReserve ?? (await getTokenBalance(deployedContractAddress, wallet.address, ethProvider)).mul(tokensToCAPercantage).div(100);

    const transaction = await tokenContract.openTrading({
        ...gas,
        nonce: nonce,
    });

    toast.info(`Open trading tx sent: ${transaction.hash}`);
    if (sniperPrivateKeys && sniperBuyTokenAmounts) {
        if (sniperPrivateKeys.length !== sniperBuyTokenAmounts.length) {
            throw new Error('Sniper private keys and buy token amounts arrays must have the same length');
        }

        for (const [index, sniperPrivateKey] of sniperPrivateKeys.entries()) {
            const amountIn = calculateEthInForExactTokenOut(sniperBuyTokenAmounts[index], tokenReserve, ethReserve);
            await buyTokenExactOut(deployedContractAddress, amountIn, sniperBuyTokenAmounts[index], sniperPrivateKey, ethProvider, {
                maxFeePerGas: gas.maxFeePerGas
                    .mul(9)
                    .div(10)
                    .sub(index + 1),
                maxPriorityFeePerGas: gas.maxPriorityFeePerGas
                    .mul(9)
                    .div(10)
                    .sub(index + 1),
                gasLimit: gas.gasLimit,
            });
            ethReserve = ethReserve.add(amountIn);
            tokenReserve = tokenReserve.sub(sniperBuyTokenAmounts[index]);
        }
    }
    await transaction.wait();
    toast.success('Success Open Trading');
};

export const removeLimits = async (deployerPrivateKey: string, ethProvider: providers.JsonRpcProvider, deployedContractAddress: string) => {
    toast.info('Removing limits...');

    const wallet = new Wallet(deployerPrivateKey, ethProvider);

    const tokenContract = new ethers.Contract(deployedContractAddress, ERC20_TOKEN_ABI, wallet);

    const gas = await getNetworkSettings(ethProvider);
    const transaction = await tokenContract.removeLimits({ ...gas });
    await transaction.wait();

    toast.success('Success Remove Limits');

    return transaction.hash;
};

export const renounceOwnership = async (deployerPrivateKey: string, ethProvider: providers.JsonRpcProvider, deployedContractAddress: string) => {
    toast.info('Renouncing Ownership...');

    const wallet = new Wallet(deployerPrivateKey, ethProvider);

    const tokenContract = new ethers.Contract(deployedContractAddress, ERC20_TOKEN_ABI, wallet);

    const gas = await getNetworkSettings(ethProvider);
    const transaction = await tokenContract.renounceOwnership({ ...gas });
    await transaction.wait();

    toast.success('Success Renounce Ownership');

    return transaction.hash;
};

export const approveForRemoveLiquidity = async (
    tokenA: string,
    deployerPrivateKey: string,
    ethProvider: providers.JsonRpcProvider,
    remove?: boolean,
    removePercentage?: number,
) => {
    toast.info('Approving for remove liquidity...');

    const wallet = new Wallet(deployerPrivateKey, ethProvider);

    const uniswapV2Factory = new ethers.Contract(FACTORY_ADDRESS, UNISWAP_V2_FACTORY_ABI, wallet);

    const pairAddress = await uniswapV2Factory.getPair(tokenA, WETH_ADDRESS);

    console.log(`pairAddress: ${pairAddress}`);
    const pair = new ethers.Contract(pairAddress, UNISWAP_V2_PAIR_ABI, wallet);
    const gas = await getNetworkSettings(ethProvider);
    const nonce = await wallet.provider.getTransactionCount(wallet.address);
    const transaction = await pair.approve(ROUTER_ADDRESS, ethers.constants.MaxUint256, { ...gas, nonce: nonce });
    toast.info(`Transaction sent: ${transaction.hash}`);

    if (remove && removePercentage)
        removeLiquidity(
            tokenA,
            removePercentage,
            deployerPrivateKey,
            ethProvider,
            { maxFeePerGas: gas.maxFeePerGas.sub(1), maxPriorityFeePerGas: gas.maxPriorityFeePerGas.sub(1), gasLimit: gas.gasLimit },
            nonce + 1,
        );
    await transaction.wait();
    toast.success('Success Approve for Remove Liquidity');

    return transaction.hash;
};

export const removeLiquidity = async (
    tokenA: string,
    removePercentage: number,
    deployerPrivateKey: string,
    ethProvider: providers.JsonRpcProvider,
    gas?: NetworkSettings,
    nonce?: number,
) => {
    toast.info('Removing liquidity...');
    const wallet = new Wallet(deployerPrivateKey, ethProvider);
    const uniswapV2Router = new ethers.Contract(ROUTER_ADDRESS, UNISWAP_V2_ROUTER_ABI, wallet);

    const pairAddress = await getPairAddressFromTokenAddress(tokenA, ethProvider);
    const pair = new ethers.Contract(pairAddress, UNISWAP_V2_PAIR_ABI, wallet);

    const balance = await pair.balanceOf(wallet.address);
    const liquidity = balance.mul(removePercentage).div(100).toString();
    gas = gas ?? (await getNetworkSettings(ethProvider));
    nonce = nonce ?? (await wallet.provider.getTransactionCount(wallet.address));

    const transaction = await uniswapV2Router.removeLiquidityETHSupportingFeeOnTransferTokens(
        tokenA,
        liquidity,
        0,
        0,
        wallet.address,
        ethers.constants.MaxUint256,
        { ...gas, nonce: nonce },
    );
    toast.info(`Transaction sent: ${transaction.hash}`);
    await transaction.wait();

    toast.success('Success Remove Liquidity');
    return transaction.hash;
};

export const burnLpToken = async (tokenA: string, burnPercentage: number, deployerPrivateKey: string, ethProvider: providers.JsonRpcProvider) => {
    toast.info('Burning liquidity...');
    const wallet = new Wallet(deployerPrivateKey, ethProvider);

    const pairAddress = await getPairAddressFromTokenAddress(tokenA, ethProvider);
    const pair = new ethers.Contract(pairAddress, UNISWAP_V2_PAIR_ABI, wallet);

    const balance = await pair.balanceOf(wallet.address);

    const liquidity = balance.mul(burnPercentage).div(100).toString();

    console.log(`pairAddress: ${pairAddress}`);
    console.log(`balance: ${balance.toString()}`);
    console.log(`liquidity: ${liquidity.toString()}`);
    const gas = await getNetworkSettings(ethProvider);
    const tx = await pair.transfer('0x000000000000000000000000000000000000dead', liquidity, { ...gas });
    await tx.wait();
    toast.success('Success Burn LP Token');

    return tx.hash;
};

export const manualSwap = async (tokenA: string, deployerPrivateKey: string, ethProvider: providers.JsonRpcProvider) => {
    const wallet = new Wallet(deployerPrivateKey, ethProvider);
    const tokenContract = new ethers.Contract(tokenA, ERC20_TOKEN_ABI, wallet);
    const gas = await getNetworkSettings(ethProvider);
    const tx = await tokenContract.manualSwap({ ...gas });
    toast.info(`Transaction sent: ${tx.hash}`);
    await tx.wait(1);
    toast.success('Success Manual Swap');

    return tx.hash;
};

export const getPairAddressFromTokenAddress = async (tokenA: string, ethProvider: providers.JsonRpcProvider) => {
    const randomWallet = ethers.Wallet.createRandom().connect(ethProvider);
    const uniswapV2Factory = new ethers.Contract(FACTORY_ADDRESS, UNISWAP_V2_FACTORY_ABI, randomWallet);

    const pairAddress = await uniswapV2Factory.getPair(tokenA, WETH_ADDRESS);

    console.log(`#{tokenA}: ${tokenA}`);
    console.log(`pairAddress: ${pairAddress}`);

    if (pairAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error('Pair address not found!');
    }
    return pairAddress;
};

export const getTokenBalance = async (tokenAddress: string, walletAddress: string, ethProvider: providers.JsonRpcProvider) => {
    const randomWallet = ethers.Wallet.createRandom().connect(ethProvider);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_TOKEN_ABI, randomWallet);
    console.log(`tokenAddress: ${tokenAddress}`);
    console.log(`walletAddress: ${walletAddress}`);
    const balance: BigNumber = await tokenContract.balanceOf(walletAddress);

    return balance;
};

export const getTokenBalanceUI = async (tokenAddress: string, walletAddress: string, ethProvider: providers.JsonRpcProvider) => {
    console.log(`getTokenBalanceUI: tokenAddress: ${tokenAddress}, walletAddress: ${walletAddress}`);
    const [balance, decimals] = await Promise.all([getTokenBalance(tokenAddress, walletAddress, ethProvider), getTokenDecimals(tokenAddress, ethProvider)]);

    return Number(ethers.utils.formatUnits(balance, decimals));
};

export const getTokenDecimals = async (tokenAddress: string, ethProvider: providers.JsonRpcProvider): Promise<number> => {
    const randomWallet = ethers.Wallet.createRandom().connect(ethProvider);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_TOKEN_ABI, randomWallet);
    const decimals: number = await tokenContract.decimals();

    return decimals;
};

export const buyTokenExactOut = async (
    tokenOut: string,
    amountIn: BigNumber,
    amountOut: BigNumber,
    buyerPk: string,
    ethProvider: providers.JsonRpcProvider,
    gas: NetworkSettings,
) => {
    const wallet = new Wallet(buyerPk, ethProvider);
    const router = new ethers.Contract(ROUTER_ADDRESS, UNISWAP_V2_ROUTER_ABI, wallet);

    const path = [WETH_ADDRESS, tokenOut];

    console.log(
        `Buying token: ${tokenOut}, amountIn: ${amountIn.toString()}, amountOut: ${amountOut.toString()}, wallet: ${wallet.address}, gas: ${JSON.stringify(
            gas,
        )}`,
    );

    const tx = await router.swapETHForExactTokens(amountOut, path, wallet.address, ethers.constants.MaxUint256, {
        value: amountIn.add(ethers.utils.parseEther('0.01')),
        ...gas,
    });

    toast.info(`Snipe transaction sent: ${tx.hash}`);
    return tx;
};
export async function sellExactIn(
    tokenIn: string,
    amountIn: BigNumber,
    ownerPK: string,
    ethProvider: providers.JsonRpcProvider,
    nonce?: number,
    gas?: NetworkSettings,
) {
    const wallet = new Wallet(ownerPK, ethProvider);
    const router = new ethers.Contract(ROUTER_ADDRESS, UNISWAP_V2_ROUTER_ABI, wallet);

    const path = [tokenIn, WETH_ADDRESS];

    gas = gas ?? (await getNetworkSettings(ethProvider));

    const tx = await router.swapExactTokensForETHSupportingFeeOnTransferTokens(amountIn, 0, path, wallet.address, ethers.constants.MaxUint256, {
        ...gas,
        nonce: nonce,
    });

    return tx.hash;
}

export function calculateEthInForExactTokenOut(tokenOut: BigNumber, tokenReserve: BigNumber, ethReserve: BigNumber): BigNumber {
    if (tokenOut.gte(tokenReserve)) {
        throw new Error('Trying to buy more tokens than available in the pool');
    }

    const numerator = ethReserve.mul(tokenOut).mul(1000);
    const denominator = tokenReserve.sub(tokenOut).mul(997);

    return numerator.div(denominator);
}

export async function approve(
    tokenAddress: string,
    ownerPK: string,
    spender: string,
    ethProvider: providers.JsonRpcProvider,
    nonce?: number,
    gas?: NetworkSettings,
) {
    const wallet = new Wallet(ownerPK, ethProvider);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_TOKEN_ABI, wallet);

    gas = gas ?? (await getNetworkSettings(ethProvider));

    const tx = await tokenContract.approve(spender, ethers.constants.MaxUint256, {
        ...gas,
        nonce: nonce,
    });

    return tx.hash;
}

export async function collectEthMultiple(wallets: Wallet[], receiver: string, provider: providers.JsonRpcProvider) {
    const gasPrice = (await provider.getFeeData()).gasPrice?.mul(125).div(100);
    if (!gasPrice) {
        throw new Error('Failed to fetch gas price');
    }

    const estimateTx = {
        to: receiver,
        value: ethers.utils.parseEther('0.000001'),
        gasLimit: 21000,
        gasPrice: gasPrice,
    };
    const txPrice = (await provider.estimateGas(estimateTx)).mul(gasPrice);

    wallets.forEach(async (wallet) => {
        const balance = await provider.getBalance(wallet.address);
        const receiveAmount = balance.sub(txPrice);
        transferSingle(wallet, receiver, receiveAmount, gasPrice);
    });
}

export async function disperseEthMultiple(wallet: Wallet, receivers: string[], amounts: BigNumber[], provider: providers.JsonRpcProvider) {
    console.log(
        'Dispersing multiple amounts:',
        receivers,
        amounts.map((amount) => amount.toString()),
    );
    const gasPrice = (await provider.getFeeData()).gasPrice?.mul(125).div(100);
    if (!gasPrice) {
        throw new Error('Failed to fetch gas price');
    }

    const totalAmount = amounts.reduce((acc, amount) => acc.add(amount), BigNumber.from(0));
    const balance = await provider.getBalance(wallet.address);
    if (balance.lt(totalAmount)) {
        toast.error('INSUFFICIENT BALANCE ON THE WALLET');
        return;
    }

    const nonce = await provider.getTransactionCount(wallet.address);

    for (let i = 0; i < receivers.length; i++) {
        await transferSingle(wallet, receivers[i], amounts[i], gasPrice, nonce + i);
    }
}

export async function transferSingle(wallet: Wallet, receiver: string, receiveAmount: BigNumber, gasPrice: BigNumber, nonce?: number) {
    if (receiveAmount.lte(0)) {
        throw new Error('INSUFFICIENT BALANCE');
    }

    const tx = {
        to: receiver,
        value: receiveAmount,
        gasLimit: BigInt(21_000),
        gasPrice: gasPrice,
        nonce: nonce,
    };

    const transactionResponse = await wallet.sendTransaction(tx);
    toast.info(`Transfer transaction sent: ${transactionResponse.hash}`);
    return transactionResponse.hash;
}

export async function approveAndSell(PKs: string[], tokenAddress: string, provider: providers.JsonRpcProvider) {
    const gas = await getNetworkSettings(provider);

    const results = await Promise.all(
        PKs.map(async (pk) => {
            const wallet = new Wallet(pk, provider);
            const balance = await getTokenBalance(tokenAddress, wallet.address, provider);
            const nonce = await wallet.provider.getTransactionCount(wallet.address);

            const approveTx = await approve(tokenAddress, pk, ROUTER_ADDRESS, provider, nonce, gas);
            toast.info(`Approve transaction sent: ${approveTx.hash}`);

            const sellTx = await sellExactIn(tokenAddress, balance, pk, provider, nonce + 1, {
                maxFeePerGas: gas.maxFeePerGas.sub(1),
                maxPriorityFeePerGas: gas.maxPriorityFeePerGas.sub(1),
                gasLimit: gas.gasLimit,
            });
            toast.info(`Sell transaction sent: ${sellTx.hash}`);

            return { approveHash: approveTx.hash, sellHash: sellTx.hash };
        }),
    );

    const approveHashes = results.map((r) => r.approveHash);
    const sellHashes = results.map((r) => r.sellHash);

    console.log(`Approve hashes: ${approveHashes}`);
    console.log(`Sell hashes: ${sellHashes}`);

    return { approveHashes, sellHashes };
}
